// ----------------------------------------------------------------------------
// Snippet: Announcement Bar
// ----------------------------------------------------------------------------
//
// ----------------------------------------------------------------------------

import Swiper from 'swiper';
import { A11y, Autoplay } from 'swiper/modules';

// Styles
import './_announcement-bar.scss';
import 'swiper/css';
var ANNOUNCEMENT_BAR_SECTION_SELECTOR = '.section--announcement-bar';
var ANNOUNCEMENT_BAR_ROOT_SELECTOR = '.js-announcement-bar';
var ANNOUNCEMENT_BAR_CAROUSEL_SELECTOR = '.js-announcement-bar-carousel';
var ANNOUNCEMENT_BAR_TRACK_SELECTOR = '.js-announcement-bar-track';
var ANNOUNCEMENT_BAR_TRIGGER_SELECTOR = '.js-announcement-bar-trigger';
var ANNOUNCEMENT_BAR_DROPDOWN_SELECTOR = '.js-announcement-bar-dropdown';
var ANNOUNCEMENT_BAR_DROPDOWN_CLOSE_SELECTOR = '.js-announcement-bar-dropdown-close';
var ANNOUNCEMENT_PLAY_PAUSE_SELECTOR = '.announcement-play-pause';
var DROPDOWN_OPEN_CLASS = 'announcement-bar__dropdown--open';
/** Keep in sync with `--announcement-bar-dropdown-duration` in _announcement-bar.scss (+ ~50ms). */
var DROPDOWN_CLOSE_MS = 400;
var announcementBarState = {
  slider: null,
  userPaused: false,
  playPauseButton: null
};
function setPlayPauseButtonState(button, isPaused) {
  if (!button) return;
  var pauseIcon = button.querySelector('.announcement-play-pause__icon--pause');
  var playIcon = button.querySelector('.announcement-play-pause__icon--play');
  button.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
  pauseIcon === null || pauseIcon === void 0 || pauseIcon.classList.toggle('is-active', !isPaused);
  playIcon === null || playIcon === void 0 || playIcon.classList.toggle('is-active', isPaused);
}
function syncSliderStateWithDropdown(isDropdownOpen) {
  var _announcementBarState;
  if (!((_announcementBarState = announcementBarState.slider) !== null && _announcementBarState !== void 0 && _announcementBarState.autoplay)) return;
  var shouldPause = isDropdownOpen || announcementBarState.userPaused;
  if (!shouldPause) {
    announcementBarState.slider.autoplay.start();
    setPlayPauseButtonState(announcementBarState.playPauseButton, false);
    return;
  }
  announcementBarState.slider.autoplay.stop();
  setPlayPauseButtonState(announcementBarState.playPauseButton, true);
}
function initAnnouncementBarCarousel(section) {
  var carouselEl = section.querySelector(ANNOUNCEMENT_BAR_CAROUSEL_SELECTOR);
  if (!carouselEl || carouselEl.classList.contains('swiper-initialized')) return;
  if (!carouselEl.classList.contains('swiper')) return;
  var trackEl = carouselEl.querySelector(ANNOUNCEMENT_BAR_TRACK_SELECTOR);
  if (!trackEl) return;
  var announcementSlider = new Swiper(carouselEl, {
    modules: [A11y, Autoplay],
    slidesPerView: 1,
    a11y: true,
    loop: true,
    autoplay: {
      delay: 5000
    },
    watchOverflow: true,
    observer: true,
    observeParents: true
  });
  announcementBarState.slider = announcementSlider;
  var playPauseButton = section.querySelector(ANNOUNCEMENT_PLAY_PAUSE_SELECTOR);
  announcementBarState.playPauseButton = playPauseButton;
  if (!playPauseButton) return;
  setPlayPauseButtonState(playPauseButton, false);
  playPauseButton.addEventListener('click', () => {
    var isManuallyPaused = playPauseButton.getAttribute('aria-pressed') === 'true';
    if (isManuallyPaused) {
      announcementSlider.autoplay.start();
      announcementBarState.userPaused = false;
      setPlayPauseButtonState(playPauseButton, false);
    } else {
      announcementSlider.autoplay.stop();
      announcementBarState.userPaused = true;
      setPlayPauseButtonState(playPauseButton, true);
    }
  });
}
function initAnnouncementBarDropdown(section) {
  var root = section.querySelector(ANNOUNCEMENT_BAR_ROOT_SELECTOR);
  var trigger = section.querySelector(ANNOUNCEMENT_BAR_TRIGGER_SELECTOR);
  var dropdown = section.querySelector(ANNOUNCEMENT_BAR_DROPDOWN_SELECTOR);
  var closeButton = section.querySelector(ANNOUNCEMENT_BAR_DROPDOWN_CLOSE_SELECTOR);
  if (!root || !trigger || !dropdown) return;
  if (root.dataset.dropdownBound === 'true') return;
  root.dataset.dropdownBound = 'true';
  var reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var timer = null;
  var clearTimer = () => {
    if (timer != null) clearTimeout(timer);
    timer = null;
  };
  var closeDropdown = () => {
    clearTimer();
    if (trigger.getAttribute('aria-expanded') !== 'true' && dropdown.hidden) {
      return;
    }
    trigger.setAttribute('aria-expanded', 'false');
    syncSliderStateWithDropdown(false);
    var wasAnimating = dropdown.classList.contains(DROPDOWN_OPEN_CLASS);
    dropdown.classList.remove(DROPDOWN_OPEN_CLASS);
    if (!wasAnimating || reduceMotion()) {
      dropdown.hidden = true;
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      dropdown.hidden = true;
    }, DROPDOWN_CLOSE_MS);
  };
  var openDropdown = () => {
    clearTimer();
    dropdown.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    syncSliderStateWithDropdown(true);
    var addOpenClass = () => {
      timer = null;
      if (trigger.getAttribute('aria-expanded') === 'true') {
        dropdown.classList.add(DROPDOWN_OPEN_CLASS);
      }
    };
    if (reduceMotion()) addOpenClass();else timer = setTimeout(addOpenClass, 0);
  };
  trigger.addEventListener('click', () => {
    if (trigger.getAttribute('aria-expanded') === 'true') {
      closeDropdown();
    } else {
      openDropdown();
    }
  });
  closeButton === null || closeButton === void 0 || closeButton.addEventListener('click', closeDropdown);
  document.addEventListener('click', event => {
    if (!root.contains(event.target)) closeDropdown();
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') closeDropdown();
  });
}
function mountAnnouncementBarCarousel() {
  var _root$matches, _root$querySelector;
  var root = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  var section = (_root$matches = root.matches) !== null && _root$matches !== void 0 && _root$matches.call(root, ANNOUNCEMENT_BAR_SECTION_SELECTOR) ? root : (_root$querySelector = root.querySelector) === null || _root$querySelector === void 0 ? void 0 : _root$querySelector.call(root, ANNOUNCEMENT_BAR_SECTION_SELECTOR);
  if (!section) return;
  initAnnouncementBarCarousel(section);
  initAnnouncementBarDropdown(section);
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAnnouncementBarCarousel);
} else {
  mountAnnouncementBarCarousel();
}
document.addEventListener('shopify:section:load', event => {
  mountAnnouncementBarCarousel(event.target);
});